import FileSize from "../../../../components/commonComponent/FileSize";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import {
  ArrowRight,
  Download,
  FileSpreadsheet,
  Minus,
  Plus,
} from "lucide-react";
import closeIcon from "../../../../assets/pages/landing/close.svg";
import { requestExternalNavigation } from "../../../../utils/externalNavigation";

const PDF_ZOOM_MIN = 50;
const PDF_ZOOM_MAX = 200;
const PDF_ZOOM_STEP = 25;

export const PdfBadge = () => (
  <span className="relative inline-block h-7 w-6 shrink-0 rounded-[3px] bg-[#dfe5ea] shadow-[0_1px_2px_rgba(0,0,0,0.16)]">
    <span className="absolute right-0 top-0 h-0 w-0 border-b-[8px] border-l-[8px] border-b-[#c7d0d8] border-l-transparent" />
    <span className="absolute left-1/2 top-[13px] -translate-x-1/2 whitespace-nowrap rounded-[2px] bg-[#ff5145] px-[3px] py-[1px] text-[8px] font-bold leading-none text-white">
      PDF
    </span>
  </span>
);

const SpreadsheetBadge = () => (
  <span className="inline-flex h-7 w-6 shrink-0 items-center justify-center rounded-[3px] bg-[#e7f6ec] text-[#139447] shadow-[0_1px_2px_rgba(0,0,0,0.16)]">
    <FileSpreadsheet size={18} strokeWidth={2.2} aria-hidden="true" />
  </span>
);

// Displays PDF preview with zoom and document controls.
export const PdfPreviewModal = ({ pdfDocument, onClose }) => {
  const [zoom, setZoom] = useState(100);

  // Decreases PDF content zoom within allowed limits.
  const zoomOut = () => {
    setZoom((currentZoom) =>
      Math.max(PDF_ZOOM_MIN, currentZoom - PDF_ZOOM_STEP),
    );
  };

  // Increases PDF content zoom within allowed limits.
  const zoomIn = () => {
    setZoom((currentZoom) =>
      Math.min(PDF_ZOOM_MAX, currentZoom + PDF_ZOOM_STEP),
    );
  };

  useEffect(() => {
    // Keep the page visible under the dim overlay.
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[30000] flex items-center justify-center bg-black/35 px-3 py-4 sm:px-6"
      onClick={onClose}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="rti-pdf-preview-title"
        className="flex h-[82vh] w-full max-w-[1100px] flex-col overflow-hidden rounded-[4px] border border-[#dfe5ea] bg-white shadow-[0_30px_80px_rgba(0,0,0,0.28)]"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="relative flex min-h-[86px] shrink-0 items-start justify-between gap-5 px-6 pt-7 sm:px-8">
          <div className="min-w-0 pr-4">
            <h2
              id="rti-pdf-preview-title"
              className="m-0 text-[16px] font-bold leading-6 text-[#005856]"
            >
              {pdfDocument.title}
              <FileSize href={pdfDocument.href} fileName={pdfDocument.fileName} />
            </h2>

          </div>

          <div className="flex shrink-0 items-center gap-2">
            <div
              className="inline-flex h-10 items-center overflow-hidden rounded-full border border-[#123764] bg-white text-[#123764]"
              role="group"
              aria-label="PDF zoom controls"
            >
              <button
                type="button"
                onClick={zoomOut}
                disabled={zoom === PDF_ZOOM_MIN}
                title="Zoom out"
                aria-label="Zoom out"
                className="inline-flex h-full w-9 items-center justify-center transition-colors hover:bg-[#eef5ff] focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#0f8a7e] disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Minus size={18} strokeWidth={2.2} aria-hidden="true" />
              </button>

              <span
                className="min-w-[48px] border-x border-[#c9d5e2] px-1 text-center text-[13px] font-semibold leading-10"
                aria-live="polite"
              >
                {zoom}%
              </span>

              <button
                type="button"
                onClick={zoomIn}
                disabled={zoom === PDF_ZOOM_MAX}
                title="Zoom in"
                aria-label="Zoom in"
                className="inline-flex h-full w-9 items-center justify-center transition-colors hover:bg-[#eef5ff] focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#0f8a7e] disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Plus size={18} strokeWidth={2.2} aria-hidden="true" />
              </button>
            </div>

            <a
              href={pdfDocument.href}
              download={pdfDocument.fileName || undefined}
              title="Download PDF"
              aria-label={`Download ${pdfDocument.title}`}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#123764] bg-white text-[#123764] transition-colors hover:bg-[#eef5ff] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0f8a7e] focus-visible:ring-offset-2"
            >
              <Download size={22} strokeWidth={2} />
            </a>

            <button
              type="button"
              onClick={onClose}
              aria-label="Close PDF preview"
              className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white transition-colors hover:bg-[#eef5ff] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0f8a7e] focus-visible:ring-offset-2"
            >
              <img src={closeIcon} alt="" className="h-10 w-10" />
            </button>
          </div>
        </header>

        <div className="min-h-0 flex-1 bg-white px-6 pb-3 sm:px-11">
          <iframe
            key={`${pdfDocument.href}-${zoom}`}
            src={`${pdfDocument.href}#toolbar=0&navpanes=0&scrollbar=1&zoom=${zoom}`}
            title={pdfDocument.title}
            className="h-full w-full border-0 bg-white"
          />
        </div>
      </section>
    </div>
  );
};

const PdfDocumentTable = ({
  documents,
  actionHeader = "File",
  titleHeader = "Title",
  emptyMessage = "No documents available.",
  minWidth = "720px",
  showMissingPdfBadge = false,
  showSerial = true,
}) => {
  const navigate = useNavigate();
  const [selectedDocument, setSelectedDocument] = useState(null);

  const handleOpenDocument = (document) => {
    if (document.path) {
      navigate(document.path);
      return;
    }

    if (document.external && document.href) {
      requestExternalNavigation(document.href);
      return;
    }

    setSelectedDocument(document);
  };

  const handleCloseDocument = () => {
    setSelectedDocument(null);
  };

  return (
    <>
      <section className="rounded-2xl mt-5 bg-white p-4 shadow-[0_10px_30px_rgba(0,0,0,0.04)]">
        {documents.length === 0 ? (
          <div className="rounded-xl border border-dashed border-[#cad8e6] bg-[#f8fbfd] px-5 py-8 text-center text-[15px] font-medium text-[#0f5c66]">
            {emptyMessage}
          </div>
        ) : (
          <div className="overflow-hidden rounded-xl border border-[#d9e4ef]">
            <div className="overflow-x-auto">
              <table
                className="w-full table-fixed border-collapse text-[13px] text-[#001b33]"
                style={{ minWidth }}
              >
                <colgroup>
                  {showSerial && <col className="w-[72px]" />}
                  <col />
                  <col className="w-[260px]" />
                </colgroup>
                <thead>
                  <tr className="bg-[#dce9fb] text-left text-[14px] font-bold text-[#001b33]">
                    {showSerial && <th className="px-4 py-3">S.No.</th>}
                    <th className="px-4 py-3">{titleHeader}</th>
                    <th className="px-4 py-3 text-left">{actionHeader}</th>
                  </tr>
                </thead>
                <tbody>
                  {documents.map((document, index) => (
                    <tr
                      key={`${document.title}-${document.serial}`}
                      className={`border-b border-[#d9e4ef] last:border-b-0 ${
                        index % 2 === 0 ? "bg-[#fbfdff]" : "bg-[#f5f9fc]"
                      }`}
                    >
                      {showSerial && (
                        <td className="px-4 py-2.5 align-middle font-medium">
                          {String(document.serial).padStart(2, "0")}
                        </td>
                      )}
                      <td className="max-w-[760px] px-4 py-2.5 align-middle font-medium">
                        <span
                          className="block whitespace-normal break-words"
                          title={document.title}
                        >
                          {document.title}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-left align-middle">
                        {document.href || document.path ? (
                          <button
                            type="button"
                            onClick={() => handleOpenDocument(document)}
                            title={document.title}
                            aria-label={`Open ${document.title}`}
                            className="grid w-full grid-cols-[40px_24px_minmax(0,1fr)] items-center gap-2 rounded-[3px] text-left text-[#16376c] outline-none transition-colors hover:text-[#0f8a7e] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0f8a7e] focus-visible:ring-offset-2"
                          >
                            <span className="text-[15px] font-medium underline underline-offset-2">
                              Open
                            </span>
                            {document.path ? (
                              <ArrowRight size={23} strokeWidth={2.2} />
                            ) : document.fileType === "spreadsheet" ? (
                              <SpreadsheetBadge />
                            ) : (
                              <PdfBadge />
                            )}
                            {!document.path && document.fileType !== "spreadsheet" && (<FileSize href={document.href} fileName={document.fileName} />)}
                          </button>
                        ) : (
                          <span
                            title="PDF file not available"
                            className="grid w-full grid-cols-[40px_24px_minmax(0,1fr)] items-center gap-2 text-left text-[#16376c]"
                          >
                            <span className="text-[15px] font-medium underline underline-offset-2">
                              Open
                            </span>
                            {showMissingPdfBadge ? (
                              <PdfBadge />
                            ) : (
                              <ArrowRight size={23} strokeWidth={2.2} />
                            )}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>

      {selectedDocument && (
        <PdfPreviewModal
          pdfDocument={selectedDocument}
          onClose={handleCloseDocument}
        />
      )}
    </>
  );
};

export default PdfDocumentTable;
